import {
  safePolygon,
  useClick,
  useDismiss,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions';
import {
  AutoPosition,
  Options as AutoPositionOptions,
  isEnabled,
  useAutoPosition,
} from './useAutoPosition';

type Role =
  | 'dialog'
  | 'alertdialog'
  | 'tooltip'
  | 'menu'
  | 'listbox'
  | 'grid'
  | 'tree';

const DefaultHoverConfig = {
  enabled: true as boolean | undefined,
  delay: null as Partial<{ open: number; close: number }> | number | null,
  interactive: true,
};
/**
 * Open the floating object on trigger hover.
 * * delay: Delay of the opening and closing. Default: 0
 * * interactive: Is tooltip interactive. Should not close if receiving hover. Default: Enabled
 */
type HoverConfig = Partial<typeof DefaultHoverConfig> | null | false;

const DefaultRoleConfig = {
  enabled: false,
  role: null as Role | null,
};
/**
 *  Add a11y parameters to a floating object automatically based on the role
 *  Default: Disabled
 */
type RoleConfig = Partial<typeof DefaultRoleConfig> | null | false;

const DefaultClickConfig = {
  enabled: false,
};
/**
 *  Auto open on click.
 *  Default: Disabled
 */
type ClickConfig = Partial<typeof DefaultClickConfig> | null | false;

const DefaultFocusConfig = {
  enabled: true,
};
/**
 *  Auto open on focus.
 *  Default: Enabled
 */
type FocusConfig = Partial<typeof DefaultFocusConfig> | null | false;

const DefaultDismissConfig = {
  enabled: true,
};
/**
 *  Auto close.
 *  Default: Enabled
 */
type DismissConfig = Partial<typeof DefaultDismissConfig> | null | false;

type AutoInteractionsOptions = {
  // Auto open on hover. Default: Enabled
  hover?: HoverConfig;
  // Auto open on click.  Default: Disabled
  click?: ClickConfig;
  // Auto open on focus.  Default: Enabled
  focus?: FocusConfig;
  // Set role to automatically add a11y params to floater. Default: Disabled
  role?: RoleConfig;
  // Auto close.  Default: Enabled
  dismiss?: DismissConfig;
};

export type Options = AutoPositionOptions & AutoInteractionsOptions;

type AutoInteration<T extends HTMLElement, Y extends HTMLElement> = Omit<
  AutoPosition<T, Y>,
  '_context' | '_open'
> & {
  open: boolean;
  getTriggerProps: (
    userProps?: React.HTMLProps<Element> | undefined,
  ) => Record<string, unknown>;
  getFloatProps: (
    userProps?: React.HTMLProps<HTMLElement> | undefined,
  ) => Record<string, unknown>;
};

/**
 * Wrap above useAutoPosition with interaction controls.
 * Based on the config updated open property
 * @param config Options to modify the behaviour
 */
export const useAutoInteractions = <
  T extends HTMLElement = HTMLElement,
  Y extends HTMLElement = HTMLElement,
>(
  config: Partial<Options> = {},
): AutoInteration<T, Y> => {
  const { hover, click, focus, dismiss, role, ...autoPositionConfig } = config;
  const options: AutoInteractionsOptions = {
    hover: isEnabled(hover, true) && {
      ...DefaultHoverConfig,
      ...hover,
    },
    click: isEnabled(click, true) && {
      ...DefaultClickConfig,
      ...config.click,
    },
    focus: isEnabled(focus, true) && {
      ...DefaultFocusConfig,
      ...focus,
    },
    role: isEnabled(role, true) && {
      ...DefaultRoleConfig,
      ...role,
    },
    dismiss: isEnabled(dismiss, true) && {
      ...DefaultDismissConfig,
      ...dismiss,
    },
  };
  const {
    _context: context,
    _open: open,
    ...rest
  } = useAutoPosition<T, Y>(autoPositionConfig);
  const hoverHook = useHover(context, {
    ...(options.hover || { enabled: false }),
    delay: (options.hover && options.hover.delay) || undefined,
    handleClose:
      options.hover && options.hover.interactive ? safePolygon() : null,
  });
  const focusHook = useFocus(context, {
    ...(options.focus || { enabled: false }),
  });
  const clickHook = useClick(context, {
    ...(options.click || { enabled: false }),
  });
  const dismissHook = useDismiss(context, {
    ...(options.dismiss || { enabled: false }),
  });
  const roleHook = useRole(context, {
    ...(options.role || { enabled: false }),
    role: (options.role && options.role.role) || undefined,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hoverHook,
    focusHook,
    roleHook,
    dismissHook,
    clickHook,
  ]);

  return {
    open,
    getTriggerProps: getReferenceProps,
    getFloatProps: getFloatingProps,
    ...rest,
  };
};

export default useAutoInteractions;
